'use client';

import {useEffect, useState} from 'react';
import {InterviewGuideData} from 'iq-interview_guides/types';
import {CompanyLogo} from 'iq-interview_guides/components/company_logo';
import {HeroMeta} from 'iq-interview_guides/components/company_position_guides/hero_meta';

export function HeroHeading({interviewGuide}: {interviewGuide: InterviewGuideData}) {
  const [h1El, setH1El] = useState<HTMLHeadingElement | null>(null);
  const [lineCount, setLineCount] = useState(1);

  useEffect(() => {
    if (!h1El) return;
    function measure() {
      const lineHeight = parseFloat(getComputedStyle(h1El!).lineHeight);
      if (!lineHeight) return;
      setLineCount(Math.round(h1El!.scrollHeight / lineHeight));
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(h1El);
    return () => observer.disconnect();
  }, [h1El]);

  const logo = interviewGuide.data.hide_company_logo ? null : (
    <CompanyLogo
      companyWebsite={interviewGuide.company_website}
      companyName={interviewGuide.company_name}
      size={56}
      className='rounded-xl object-contain bg-white'
      wrapperClassName='bg-white rounded-2xl p-2 shadow-md shrink-0'
    />
  );

  const meta = (
    <HeroMeta
      experiences={interviewGuide.experiences}
      questions={interviewGuide.questions}
      updated_at={interviewGuide.updated_at}
      reviewer={interviewGuide.reviewer}
    />
  );

  const title = (
    <h1 ref={setH1El} className='text-3xl max-[500px]:text-2xl font-bold leading-tight'>
      {interviewGuide.company_name} {interviewGuide.position_name} Interview Guide
    </h1>
  );

  // 2 lines: meta below logo + title
  if (lineCount === 2) {
    return (
      <div className='flex flex-col gap-y-2 mb-6'>
        <div className='flex items-center gap-x-4'>
          {logo}
          {title}
        </div>
        {meta}
      </div>
    );
  }

  // 3+ lines: logo pins to top
  if (lineCount >= 3) {
    return (
      <div className='flex flex-col gap-y-2 mb-6'>
        <div className='flex items-start gap-x-4'>
          {logo}
          {title}
        </div>
        {meta}
      </div>
    );
  }

  // 1 line (default/SSR): logo row-spans title + meta so meta sits beside it
  return (
    <div className='grid grid-cols-[auto_1fr] gap-x-4 mb-6'>
      <div className='row-span-2 self-center'>{logo}</div>
      {title}
      <div className='mt-1'>{meta}</div>
    </div>
  );
}
